import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

// Parado por 8 s, a tela passa a ajudar: o botão pulsa e a dica fica mais forte.
const IDLE_MS = 8000;
// Folga de arredondamento do navegador ao chegar no fim da rolagem.
const END_TOLERANCE_PX = 10;

/**
 * Leitura de um termo até o fim sem travar quem não entende a rolagem: em vez
 * de um botão "apagado" até rolar, o botão leva ao fim num toque (`jumpToEnd`)
 * e só então vira o aceite. Mesma lógica do ConsentModal do CRM.
 *
 * `content` é o termo já carregado: a medida é refeita quando ele troca (um
 * termo que cabe inteiro na área nunca dispara onScroll e já nasce no fim).
 */
export function useReadToEnd(content: unknown) {
  const ref = useRef<HTMLDivElement>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const [atEnd, setAtEnd] = useState(false);
  const [idle, setIdle] = useState(false);

  const wake = useCallback(() => {
    setIdle(false);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setIdle(true), IDLE_MS);
  }, []);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setAtEnd(
      el.scrollTop + el.clientHeight >= el.scrollHeight - END_TOLERANCE_PX,
    );
  }, []);

  useLayoutEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, content]);

  useEffect(() => {
    wake();
    return () => clearTimeout(idleTimer.current);
  }, [wake, content]);

  const onScroll = useCallback(() => {
    measure();
    wake();
  }, [measure, wake]);

  const jumpToEnd = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    wake();
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    if (el.scrollTo) {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: reduceMotion ? "auto" : "smooth",
      });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [wake]);

  return { ref, atEnd, idle, onScroll, jumpToEnd, wake };
}
